// User-facing messages for Firebase email/password errors. None of them may
// reveal whether an account exists for an email: a wrong email and a wrong
// password get the same message, and a reset request always "succeeds".
// Raw Firebase messages are never shown (they can include the error code).
import { PASSWORD_MIN_LENGTH } from "./passwordRules";

export const SIGN_IN_FAILED = "Email or password is incorrect.";
export const RESET_SENT =
  "If an account exists for this email, we've sent a password reset link. Check your inbox and spam folder.";

const INVALID_EMAIL = "Enter a valid email address.";
const NETWORK =
  "Network error. Please check your internet connection and try again.";
const WEAK_PASSWORD = `Use at least ${PASSWORD_MIN_LENGTH} characters for your password.`;

const SIGN_IN_CREDENTIAL_CODES = new Set([
  "auth/user-not-found",
  "auth/wrong-password",
  "auth/invalid-credential",
  "auth/invalid-login-credentials",
  "auth/user-disabled",
  "auth/missing-password",
]);

export function signInErrorMessage(code) {
  if (SIGN_IN_CREDENTIAL_CODES.has(code)) return SIGN_IN_FAILED;
  if (code === "auth/invalid-email") return INVALID_EMAIL;
  if (code === "auth/too-many-requests") {
    return "Too many attempts. Please wait a few minutes and try again, or reset your password.";
  }
  if (code === "auth/network-request-failed") return NETWORK;
  return "Sign-in failed. Please try again.";
}

export function signUpErrorMessage(code) {
  if (code === "auth/email-already-in-use") {
    return "We couldn't create an account with this email. If you already have an account, sign in or reset your password.";
  }
  if (
    code === "auth/weak-password" ||
    code === "auth/password-does-not-meet-requirements"
  ) {
    return WEAK_PASSWORD;
  }
  if (code === "auth/invalid-email") return INVALID_EMAIL;
  if (code === "auth/too-many-requests") {
    return "Too many attempts. Please wait a few minutes and try again.";
  }
  if (code === "auth/network-request-failed") return NETWORK;
  return "We couldn't create your account. Please try again.";
}

// Returns { success, message } for a password reset request. "No such user"
// is reported as success so the form never confirms which emails exist.
export function passwordResetResult(code) {
  if (!code || code === "auth/user-not-found") {
    return { success: true, message: RESET_SENT };
  }
  if (code === "auth/invalid-email" || code === "auth/missing-email") {
    return { success: false, message: INVALID_EMAIL };
  }
  if (code === "auth/too-many-requests") {
    return {
      success: false,
      message: "Too many reset requests. Please try again later.",
    };
  }
  if (code === "auth/network-request-failed") {
    return { success: false, message: NETWORK };
  }
  return {
    success: false,
    message: "We couldn't send the reset email. Please try again.",
  };
}
